import * as React from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { Stack, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// --- Custom Hooks & Utils ---
import { getApiBaseUrl } from "./utils/apiUtils";
import { useAuthData } from "./hooks/useAuthData";
import { useSimulationData } from "./hooks/useSimulationData";

// --- Components ---
import AppTitle from "./components/AppTitle/AppTitle";
import Dashboard from "./components/complex/Dashboard";

// --- Pages (Keep your existing imports) ---
import DashboardHome from "./pages/dashboard/DashboardHome";
import Signup from "./pages/Signup";
import LoginPage from "./pages/Login/Login";
import ResetPasswordPage from "./pages/ResetPassword";
import LogoutPage from "./pages/Logout";
import { Info } from "./pages/Info";
import { LanderPage } from "./pages/Lander";
import { Error404 } from "./pages/Error404";
import DashboardTrainingPrograms from "./pages/dashboard/MyPrograms";
import DashboardProgram from "./pages/dashboard/Program";
import DashboardLeaderBoard from "./pages/dashboard/LeaderBoard";
import DashboardSimulations from "./pages/dashboard/Simulations";
import DashboardTeam from "./pages/dashboard/Team";
import DashboardSettings from "./pages/dashboard/Settings";
import DashboardSimulationFeedback from "./pages/dashboard/SimulationFeedback";
import TestAudioVisualizer from "./pages/TestAudio";
import { ExternalScenario } from "./pages/externalscenario/ExternalScenario";
import { ExternalScenarioDebrief } from "./pages/externalscenario/ExternalScenarioDebrief";
import { ExternalScenarioFeedback } from "./pages/externalscenario/ExternalScenarioFeedback";
import ScenarioDetails from "./pages/dashboard/ScenarioDetails";
import LiveKitTest from "./pages/LiveKitTest";
import FeedbackCoach from "./components/FeedbackCoach/FeedbackCoach";

// --- Helpers ---
import { fetchProgram } from "./components/helpers/FetchProgram";
import { fetchScenario } from "./components/helpers/FetchScenario";
import { fetchProgramFromScenarioId } from "./components/helpers/FetchProgramFromScenarioId";
import "./App.css";

export const BrandContext = React.createContext(null);

function App() {
  const [brand, setBrand] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  // UI State
  const [sideMenuOpen, setSideMenuOpen] = React.useState(true);
  const [showFooter, setShowFooter] = React.useState(true);
  const [showCrumbs, setShowCrumbs] = React.useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // 1. Get Base URL
  const api_base_url = React.useMemo(() => getApiBaseUrl(), []);

  // 2. Initialize Brand & Window Listeners
  React.useEffect(() => {
    setBrand({ api_base_url, name: "GuruNow" });
    document.title = "GuruNow";
    setLoading(false);

    let lastWidth = window.innerWidth;

    const handleResize = () => {
      const currentWidth = window.innerWidth;

      // Bail if width didn't change
      if (currentWidth === lastWidth) return;

      lastWidth = currentWidth;

      if (currentWidth < 1080) setSideMenuOpen(false);
      else setSideMenuOpen(true);
    };

    handleResize(); // Initial check
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [api_base_url]);

  // 3. Call Custom Hooks
  const {
    authToken,
    saveAuthToken,
    loggedInUser,
    trainingProgram,
    allTrainingPrograms,
  } = useAuthData(brand?.api_base_url);

  const {
    simulationsData,
    refreshSimulations,
    fetchRecentSimulationsForScenario,
  } = useSimulationData(brand?.api_base_url, authToken);

  // 4. Memoize Context Value
  const contextValue = React.useMemo(
    () => ({
      brand,
      authToken,
      saveAuthToken,
      trainingProgram,
      allTrainingPrograms,
      user: loggedInUser,
      sideMenuOpen,
      setSideMenuOpen,
      showFooter,
      setShowFooter,
      showCrumbs,
      setShowCrumbs,
      setLoading,
      simulations: simulationsData,
      refreshSimulations,
      fetchRecentSimulationsForScenario,
      isMobile,
    }),
    [
      brand,
      authToken,
      saveAuthToken,
      trainingProgram,
      allTrainingPrograms,
      loggedInUser,
      sideMenuOpen,
      showFooter,
      showCrumbs,
      simulationsData,
      refreshSimulations,
      fetchRecentSimulationsForScenario,
      isMobile,
    ]
  );

  // 5. Memoize Router Configuration
  const router = React.useMemo(() => {
    if (loading) return null;

    return createBrowserRouter([
      {
        path: "/",
        element: <Dashboard />,
        children: [{ index: true, element: <DashboardHome /> }],
      },
      // ... External Scenarios ...
      {
        path: "/externalscenario",
        children: [
          {
            path: ":scenario_id",
            element: <ExternalScenario />,
            handle: { crumb: () => "External Scenario" },
          },
        ],
      },
      {
        path: "/externalscenario_debrief/:simulation_id",
        element: <ExternalScenarioDebrief />,
        handle: { crumb: () => "Debrief" },
      },
      {
        path: "/externalscenario_feedback/:simulation_id",
        element: <ExternalScenarioFeedback />,
        handle: { crumb: () => "Feedback" },
      },
      // ... General Pages ...
      {
        path: "/info/:pagename",
        element: <Info />,
        handle: { crumb: ({ params }) => params.pagename },
      },
      { path: "/lander/:pagename", element: <LanderPage /> },
      { path: "/testaudio", element: <TestAudioVisualizer /> },
      { path: "/404", element: <Error404 /> },

      // ... Auth ...
      { path: "/signup", element: <Signup /> },
      { path: "/signup/:signupFlowName", element: <Signup /> },
      {
        path: "/login",
        element: (
          <>
            <AppTitle title="Login" />
            <LoginPage />
          </>
        ),
      },
      { path: "/reset_password", element: <ResetPasswordPage /> },
      { path: "/logout", element: <LogoutPage /> },
      { path: "/livekit/:scenario_id", element: <LiveKitTest /> },

      // ... Dashboard ...
      {
        path: "/dashboard",
        element: <Dashboard />,
        handle: { crumb: () => "Dashboard" },
        children: [
          { index: true, element: <DashboardHome /> },
          {
            path: "home",
            element: <DashboardHome />,
            handle: { crumb: () => "Home" },
          },
          {
            path: "simulations",
            element: <DashboardSimulations />,
            handle: { crumb: () => "Performance Spotlight" },
          },
          {
            path: "trainingprograms",
            element: <DashboardTrainingPrograms />,
            handle: { crumb: () => "Training Programs" },
          },
          {
            path: "team",
            element: (
              <>
                <AppTitle title="My Team" />
                <DashboardTeam />
              </>
            ),
            handle: { crumb: () => "My Team" },
          },
          {
            path: "leaderboard",
            element: <DashboardLeaderBoard />,
            handle: { crumb: () => "Leaderboard" },
          },
          {
            path: "settings",
            element: <DashboardSettings />,
            handle: { crumb: () => "Settings" },
          },
          {
            path: "trainingprogram/:trainingprogram_id",
            element: (
              <>
                <AppTitle title="My Programs" />
                <DashboardProgram />
              </>
            ),
            loader: async ({ params }) => {
              const program = await fetchProgram(
                { api_base_url },
                authToken,
                params.trainingprogram_id
              );
              return {
                programName: program.name ?? "Program",
                programUrl: `/dashboard/trainingprogram/${program?.id}`,
              };
            },
            handle: {
              crumb: (match, data) => [
                { label: "My Programs", to: "/dashboard/trainingprograms" },
                { label: data.programName, to: data.programUrl },
              ],
            },
          },
          {
            path: "simulate/:scenario_id",
            element: <ScenarioDetails />,
            loader: async ({ params }) => {
              const scenario = await fetchScenario(
                { api_base_url },
                authToken,
                params.scenario_id
              );
              const program = await fetchProgramFromScenarioId(
                { api_base_url },
                authToken,
                params.scenario_id
              );

              return {
                programName: program.name ?? "Program",
                programUrl: `/dashboard/trainingprogram/${program.id}`,
                scenarioName: scenario.name ?? "Scenario",
                scenarioUrl: `/dashboard/simulate/${scenario?.id}`,
              };
            },
            handle: {
              crumb: (match, data) => [
                {
                  label: "Training Programs",
                  to: "/dashboard/trainingprograms",
                },
                { label: data.programName, to: data.programUrl },
                { label: data.scenarioName, to: data.scenarioUrl },
              ],
            },
          },
          {
            path: "feedback/:simulation_id",
            element: <FeedbackCoach />,
            handle: {
              crumb: () => [
                {
                  label: "Performance Spotlight",
                  to: "/dashboard/simulations",
                },
                { label: "Feedback", to: "/" },
              ],
            },
          },
        ],
      },
    ]);
  }, [loading, authToken, api_base_url]);

  if (loading) {
    return (
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        sx={{ width: 1, height: "100vh" }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <React.StrictMode>
      <BrandContext.Provider value={contextValue}>
        <RouterProvider router={router} />
      </BrandContext.Provider>
    </React.StrictMode>
  );
}

export default App;
